import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from 'react-router-dom';
import '../style/loan-disbursement.css';
import {
    API_ADD_LOAN_DISBURSE,
    API_BRANCHES,
    API_CENTERS,
    API_EMPLOYEES,
    API_GET_LOANS,
    API_DISBURSEMENT_CLIENT_HISTORY
} from '../Config/ApiUrlConstant';
import { useAuth } from '../AuthContext';
import { confirmAction } from '../utils/confirmAction';
import MultipleLoanDisbursement from './MultipleLoanDisbursement';

const initialFormData = {
    branch_id: "",
    center_id: "",
    client_id: "",
    date_of_disbursement: "",
    date_of_repayment: "",
    disbursed_by_bm: "",
    disbursed_by_staff: "",
    processing_amt: "",
    insurance_amt: "",
    demand_loan_id: '',
    approve_loan_id: '',
    demand_loan_amt: '',
    approve_loan_amt: '',
    is_closed_loan: 0,
};

const SingleLoanDisbursement = ({ onShowMultiple }) => {
    const [formData, setFormData] = useState(initialFormData);
    const [clients, setClients] = useState([]);
    const [centerList, setCenterList] = useState([]);
    const [allLoans, setAllLoans] = useState([]);
    const [branches, setBranches] = useState([]);
    const [employeeList, setEmployeeList] = useState([]);
    const [supportsEmployeeRole, setSupportsEmployeeRole] = useState(false);
    const { logout } = useAuth();
    const token = localStorage.getItem('token');

    const branchManagers = supportsEmployeeRole
        ? employeeList.filter((employee) => String(employee.emp_role) === '1')
        : employeeList;
    const staffEmployees = supportsEmployeeRole
        ? employeeList.filter((employee) => String(employee.emp_role) === '2')
        : employeeList;

    const getEmployees = useCallback(async (branchId = '') => {
        try {
            const response = await fetch(API_EMPLOYEES, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify(branchId ? { branch_id: branchId } : {}),
            });
            if (response.status === 401) {
                logout();
                return;
            }
            const responseData = await response.json();
            setEmployeeList(responseData.data || []);
            setSupportsEmployeeRole(Boolean(responseData.supportsEmpRole));
        } catch (error) {
            setEmployeeList([]);
            setSupportsEmployeeRole(false);
        }
    }, [logout, token]);

    const getCentersByBranch = async (branchId) => {
        try {
            const response = await fetch(API_CENTERS, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({ is_all: false, branch_id: branchId })
            });
            if (response.status === 401) {
                logout();
                return;
            }
            const responseData = await response.json();
            setCenterList(responseData.data || []);
        } catch (error) {
            setCenterList([]);
        }
    };

    const resetForm = () => {
        setFormData(initialFormData);
        setClients([]);
        setCenterList([]);
        setAllLoans([]);
        setEmployeeList([]);
        setSupportsEmployeeRole(false);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;

        if (name === 'branch_id') {
            if (!value) {
                resetForm();
                return;
            }

            void getCentersByBranch(value);
            void getEmployees(value);
            setClients([]);
            setAllLoans([]);
            setFormData((prevData) => ({
                ...prevData,
                branch_id: value,
                center_id: "",
                client_id: "",
                disbursed_by_bm: "",
                disbursed_by_staff: "",
                demand_loan_id: "",
                approve_loan_id: "",
            }));
            return;
        }

        if (name === 'center_id' && !value) {
            setClients([]);
            setAllLoans([]);
            setFormData((prevData) => ({
                ...prevData,
                center_id: "",
                client_id: "",
                demand_loan_id: "",
                approve_loan_id: "",
            }));
            return;
        }

        if (name === 'client_id' && !value) {
            setAllLoans([]);
            setFormData((prevData) => ({
                ...prevData,
                client_id: "",
                demand_loan_id: "",
                approve_loan_id: "",
            }));
            return;
        }

        if (name === 'client_id') {
            const client = clients.find((item) => String(item.client_id) === String(value));
            setFormData((prevData) => ({ ...prevData, client_id: value, demand_loan_id: client?.demand_loan_id || '', approve_loan_id: client?.approve_loan_id || '', demand_loan_amt: client?.demand_loan_amt || '', approve_loan_amt: client?.approve_loan_amt || '' }));
            return;
        }

        setFormData((prevData) => ({
            ...prevData,
            [name]: value,
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!confirmAction('disburse this loan')) return;
        try {
            const response = await fetch(API_ADD_LOAN_DISBURSE, {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify(formData),
            });
            if (response.status === 401) {
                logout();
                return;
            }
            const result = await response.json();
            if (result.loanDisburseId > 0) {
                resetForm();
                alert("Loan disbursement details submitted successfully.");
            } else {
                alert("Failed to submit loan disbursement details.");
            }
        } catch (error) {
            alert("An error occurred. Please try again.");
        }
    };

    useEffect(() => {
        const loadMasterData = async () => {
            try {
                const response = await fetch(API_BRANCHES, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`,
                    },
                    body: JSON.stringify({}),
                });
                if (response.status === 401) {
                    logout();
                    return;
                }
                const responseData = await response.json();
                setBranches(responseData.data || []);
            } catch (error) {
                setBranches([]);
            }

            await getEmployees();
        };

        void loadMasterData();
    }, [getEmployees, logout, token]);

    useEffect(() => {
        const loadClients = async () => {
            if (!formData.center_id) {
                setClients([]);
                return;
            }

            try {
                const response = await fetch(API_DISBURSEMENT_CLIENT_HISTORY, {
                    method: "POST",
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`,
                    },
                    body: JSON.stringify({
                        center_id: formData.center_id,
                        status: 'pending'
                    }),
                });
                if (response.status === 401) {
                    logout();
                    return;
                }
                const result = await response.json();
                setClients(result.data || []);
            } catch (error) {
                alert("An error occurred. Please try again.");
            }
        };

        void loadClients();
    }, [formData.center_id, logout, token]);

    useEffect(() => {
        const loadLoans = async () => {
            if (!formData.client_id) {
                setAllLoans([]);
                return;
            }

            try {
                const response = await fetch(API_GET_LOANS, {
                    method: "POST",
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`,
                    },
                    body: JSON.stringify({
                        ...formData,
                        client_id: formData.client_id,
                        is_all: true,
                        include_inactive: true,
                    }),
                });
                if (response.status === 401) {
                    logout();
                    return;
                }
                const result = await response.json();
                setAllLoans(result.data || []);
            } catch (error) {
            }
        };

        void loadLoans();
    }, [formData, logout, token]);

    return (
        <div className="form-container">
            <div className="disbursement-heading">
                <div>
                    <h2>Single Loan Disbursement</h2>
                    <p>Enter one loan disbursement at a time.</p>
                </div>
                <button type="button" className="btn btn-outline-primary" onClick={onShowMultiple}>Multiple Disbursements</button>
            </div>
            <form onSubmit={handleSubmit}>
                <div className="form-group">
                    <label>Branch Name</label>
                    <select
                        className="form-control"
                        name="branch_id"
                        value={formData.branch_id}
                        onChange={handleChange}
                        required
                    >
                        <option value="">Select Branch</option>
                        {branches.map((branch) => (
                            <option key={branch.id} value={branch.id}>
                                {branch.branch_name}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="form-group">
                    <label>Center Name</label>
                    <select
                        className="form-control"
                        name="center_id"
                        value={formData.center_id}
                        onChange={handleChange}
                        required
                    >
                        <option value="">Select Center</option>
                        {centerList.map((center) => (
                            <option key={center.id} value={center.id}>
                                {center.center_name}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="form-group">
                    <label>Client Name</label>
                    <select
                        className="form-control"
                        name="client_id"
                        value={formData.client_id}
                        onChange={handleChange}
                        required
                    >
                        <option value="">Select Client</option>
                        {clients.map((client) => (
                            <option key={client.id} value={client.client_id}>
                                {client.client_no} - {client.client_name}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="form-group">
                    <label>Date of Disbursement</label>
                    <input
                        type="date"
                        name="date_of_disbursement"
                        value={formData.date_of_disbursement}
                        onChange={handleChange}
                        required
                    />
                </div>
                <div className="form-group">
                    <label>Date of Repayment</label>
                    <input
                        type="date"
                        name="date_of_repayment"
                        value={formData.date_of_repayment}
                        onChange={handleChange}
                        required
                    />
                </div>

                <div className="form-group">
                    <label>Disbursed By BM</label>
                    <select
                        className="form-control"
                        name="disbursed_by_bm"
                        value={formData.disbursed_by_bm}
                        onChange={handleChange}
                        required
                    >
                        <option value="">Select Branch Manager</option>
                        {branchManagers.map((employee) => (
                            <option key={employee.id} value={employee.id}>
                                {employee.emp_generate_id} - {employee.emp_name}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="form-group">
                    <label>Disbursed By Staff</label>
                    <select
                        className="form-control"
                        name="disbursed_by_staff"
                        value={formData.disbursed_by_staff}
                        onChange={handleChange}
                        required
                    >
                        <option value="">Select Staff</option>
                        {staffEmployees.map((employee) => (
                            <option key={employee.id} value={employee.id}>
                                {employee.emp_generate_id} - {employee.emp_name}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="form-group"><label>Processing Amount</label><input type="number" min="0" step="0.01" name="processing_amt" value={formData.processing_amt} onChange={handleChange} /></div>
                <div className="form-group"><label>Insurance Amount</label><input type="number" min="0" step="0.01" name="insurance_amt" value={formData.insurance_amt} onChange={handleChange} /></div>

                <div className="form-group">
                    <label>Demand Loan Amount *</label>
                    <select
                        className="form-control"
                        name="demand_loan_id"
                        value={formData.demand_loan_id}
                        onChange={handleChange}
                        required
                        disabled
                    >
                        <option value="">Select Demand Loan</option>
                        {allLoans.map((loan) => (
                            <option key={loan.id} value={loan.id}>
                                {loan.loan_amt}
                            </option>
                        ))}
                    </select>
                </div>
                <div className="form-group">
                    <label>Approved Loan Amount (Disbursed Automatically) *</label>
                    <select
                        className="form-control"
                        name="approve_loan_id"
                        value={formData.approve_loan_id}
                        onChange={handleChange}
                        required
                        disabled
                    >
                        <option value="">Select Approve Loan</option>
                        {allLoans.map((loan) => (
                            <option key={loan.id} value={loan.id}>
                                {loan.loan_amt}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="form-group">
                    <button type="submit">Submit</button>
                </div>
            </form>
        </div>
    );
};

const LoanDisbursement = ({ initialMode = 'single' }) => {
    const [disbursementMode, setDisbursementMode] = useState(initialMode);
    const navigate = useNavigate();

    useEffect(() => {
        setDisbursementMode(initialMode);
    }, [initialMode]);

    if (disbursementMode === 'multiple') {
        return <MultipleLoanDisbursement onShowSingle={() => navigate('/loan-disbursement')} />;
    }

    return <SingleLoanDisbursement onShowMultiple={() => navigate('/loan-disbursement-multiple')} />;
};

export default LoanDisbursement;
